import {
  Alert,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconFileImport } from '@tabler/icons-react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { odpriPdf } from '../api/pdf';
import { datum, datumZaApi, denar } from '../oblika/oblika';
import { imeOsebe } from '../osebe/vrste';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import {
  KlavzuleIzdaje,
  KorakiIzdaje,
  Naprej,
  OsebeIzdaje,
  StoritveIzdaje,
  TabelaStoritev,
  TabelaVozil,
  VozilaIzdaje,
  VrsticaIzdaje,
} from './Gradniki';
import { RazlikaVCeni } from './RazlikaVCeni';
import { dodajKlavzule, korakiZNapakami, korakPolja, prvaNapaka, stevilka, type Korak } from './izdaja';
import { PomocIkona } from '../pomoc/PomocIkona';

type Zahteva = Shema['RacunZahteva'];
type Osnutek = Shema['RacunOsnutek'];
type Oseba = Shema['OsebaPodatki'];

/**
 * Izdaja računa – kot KreirajRacun v Swingu (koraki Kupec, Artikli, Storitve, Roki, Predogled): nov račun
 * ({@code /racuni/nov}), po predračunu ({@code ?predracun=id}), iz delovnega naloga ({@code ?nalog=id}, modul
 * Delavnica – nalog bo ob izdaji prevzet), za hrambo gum ({@code ?gume=id}, komplet s postavko) ali urejanje ({@code /racuni/:id/uredi}). Obrazec hrani
 * samo vnos uporabnika; kar se izpelje (cene postavk, datum plačila, DDV, pripis, garancija, zneski, napake), pokaže
 * izračun strežnika ({@code POST /api/racuni/izracun}), ki ga uporabi tudi izdaja.
 */
export function NovRacun() {
  const { id } = useParams();
  const [iskanje] = useSearchParams();
  const urejani = id ? Number(id) : null;
  const predracun = iskanje.get('predracun') ? Number(iskanje.get('predracun')) : null;
  const nalog = iskanje.get('nalog') ? Number(iskanje.get('nalog')) : null;
  const gume = iskanje.get('gume') ? Number(iskanje.get('gume')) : null;
  const osnutek = useQuery({
    queryKey: ['racun-osnutek', urejani, predracun, nalog, gume],
    queryFn: ({ signal }) =>
      urejani
        ? podatki(api.GET('/api/racuni/{id}/urejanje', { signal, params: { path: { id: urejani } } }))
        : podatki(
            api.GET('/api/racuni/nov', {
              signal,
              params: {
                query: { predracun: predracun ?? undefined, nalog: nalog ?? undefined, gume: gume ?? undefined },
              },
            }),
          ),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  if (!osnutek.data) {
    return (
      <StanjeNalaganja
        napaka={osnutek.error}
        ponovi={() => void osnutek.refetch()}
        naslov={urejani ? 'Računa ni mogoče urejati' : 'Novega računa ni bilo mogoče pripraviti'}
      />
    );
  }
  return <ObrazecRacuna key={`${urejani}-${predracun}-${nalog}-${gume}`} osnutek={osnutek.data} urejani={urejani} />;
}

function ObrazecRacuna({ osnutek, urejani }: { osnutek: Osnutek; urejani: number | null }) {
  const navigate = useNavigate();
  const odjemalec = useQueryClient();
  const [vnos, setVnos] = useState<Zahteva>(() => ({
    ...osnutek.zahteva,
    poslovniProstorId: osnutek.zahteva?.poslovniProstorId ?? osnutek.furs?.poslovniProstorId,
    elektronskaNapravaId: osnutek.zahteva?.elektronskaNapravaId ?? osnutek.furs?.elektronskaNapravaId,
  }));
  const [spremenjeno, setSpremenjeno] = useState(false);
  const [korak, setKorak] = useState<Korak>(
    urejani || osnutek.zahteva?.predracunId || osnutek.zahteva?.delovniNalogId || osnutek.zahteva?.kompletGumId
      ? 'predogled'
      : 'kupec',
  );
  const [poskus, setPoskus] = useState(false);
  const [strezniske, setStrezniske] = useState<Record<string, string>>({});
  const [razlog, setRazlog] = useState<string | null>(null);
  const [dela, setDela] = useState<'predogled' | 'izdaja' | null>(null);
  const [osebe, setOsebe] = useState<{ kupec?: Oseba | null; uporabnik?: Oseba | null }>({});

  const [zakasnjen] = useDebouncedValue(vnos, 250);
  const izracun = useQuery({
    queryKey: ['izracun', 'racun', urejani, zakasnjen],
    queryFn: ({ signal }) =>
      podatki(
        api.POST('/api/racuni/izracun', {
          signal,
          body: zakasnjen,
          params: { query: { id: urejani ?? undefined } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const iz = izracun.data;
  const z = iz?.zahteva ?? vnos;
  const napake = poskus ? { ...(iz?.napake ?? {}), ...strezniske } : {};
  const aktualno = !izracun.isFetching && !izracun.isPlaceholderData && zakasnjen === vnos;

  // neshranjen vnos: brskalnik vpraša pred zaprtjem ali osvežitvijo (kot Swing ob zapiranju zavihka)
  useEffect(() => {
    if (!spremenjeno) {
      return;
    }
    const vprasaj = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', vprasaj);
    return () => window.removeEventListener('beforeunload', vprasaj);
  }, [spremenjeno]);

  const nastavi = (sprememba: Partial<Zahteva>) => {
    setVnos((v) => ({ ...v, ...sprememba }));
    setSpremenjeno(true);
    setStrezniske({});
    setRazlog(null);
  };

  // kljukica, ki jo uporabnik sme spremeniti, kaže njegov vnos (takoj ob kliku); vsiljena kaže izračun strežnika
  const izbira = (k: keyof Zahteva, omogoceno = true) => !!(omogoceno ? vnos[k] : z[k]);

  const kupec = osebe.kupec !== undefined ? osebe.kupec : iz?.kupec;
  const uporabnik = osebe.uporabnik !== undefined ? osebe.uporabnik : iz?.uporabnikVozila;
  const vozila = iz?.vozila ?? [];
  const storitve = z.storitve ?? [];
  const furs = osnutek.furs;
  const izbiraProstora = !urejani && !!vnos.posljiNaFurs && (furs?.poslovniProstori?.length ?? 0) > 1;
  const izbiraNaprave = !urejani && !!vnos.posljiNaFurs && (furs?.naprave?.length ?? 0) > 1;
  const naziv = urejani ? `Urejanje računa ${osnutek.stRacuna ?? ''}` : 'Nov račun';

  /** Prostor ali naprava se zapomni kot privzeta izbira uporabnika (kot v Swingu). */
  const izberiFurs = (sprememba: { poslovniProstorId?: number; elektronskaNapravaId?: number }) => {
    nastavi(sprememba);
    const telo = {
      poslovniProstorId: sprememba.poslovniProstorId ?? vnos.poslovniProstorId,
      elektronskaNapravaId: sprememba.elektronskaNapravaId ?? vnos.elektronskaNapravaId,
    };
    void izvedi(api.PUT('/api/furs/izbira', { body: telo })).catch(() => {
      // izbira velja za ta račun; privzeta se shrani ob naslednji spremembi
    });
  };

  const pokaziNapake = (n: Record<string, string>) => {
    setPoskus(true);
    const prva = prvaNapaka(n);
    setRazlog(prva?.sporocilo ?? null);
    if (prva) {
      setKorak(korakPolja(prva.polje));
    }
  };

  const predogled = async () => {
    setDela('predogled');
    setRazlog(null);
    try {
      await odpriPdf(
        api.POST('/api/racuni/predogled/pdf', {
          body: vnos,
          params: { query: { id: urejani ?? undefined } },
          parseAs: 'blob',
        }),
      );
    } catch (e) {
      setRazlog(sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  // kot Swing: izda (ali shrani) račun, odpre njegov PDF (poveča števec tiskanj), nato podrobnosti računa
  const izdaj = async () => {
    if (aktualno && iz && Object.keys(iz.napake ?? {}).length > 0) {
      pokaziNapake(iz.napake ?? {});
      return;
    }
    setDela('izdaja');
    setRazlog(null);
    const izid: { r?: Shema['RacunPodrobnosti'] } = {};
    try {
      await odpriPdf(
        (async () => {
          izid.r = await podatki(
            urejani
              ? api.PUT('/api/racuni/{id}', { params: { path: { id: urejani } }, body: vnos })
              : api.POST('/api/racuni', { body: vnos }),
          );
          return api.GET('/api/racuni/{id}/pdf', { params: { path: { id: izid.r.id! } }, parseAs: 'blob' });
        })(),
      );
    } catch (e) {
      if (!izid.r) {
        setDela(null);
        if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
          setStrezniske(e.polja);
          pokaziNapake(e.polja);
        } else {
          setRazlog(sporocilo(e));
        }
        return;
      }
      notifications.show({ color: 'red', message: `PDF računa ${izid.r.stRacuna} ni bil odprt: ${sporocilo(e)}` });
    }
    const r = izid.r!;
    try {
      await izvedi(api.POST('/api/racuni/po-stevilki/natisnjen', { params: { query: { stevilka: r.stRacuna! } } }));
    } catch {
      // števec tiskanj ni bistven; račun je izdan
    }
    setSpremenjeno(false);
    await Promise.all(
      ['racuni', 'racun', 'vozilo', 'vozila', 'predracun', 'predracuni', 'delovni-nalog', 'delovni-nalogi', 'gume'].map(
        (k) => odjemalec.invalidateQueries({ queryKey: [k] }),
      ),
    );
    notifications.show({
      color: 'green',
      message: urejani ? `Račun ${r.stRacuna} je shranjen.` : `Račun ${r.stRacuna} je izdan.`,
    });
    if (r.posljiNaFurs && !r.eor) {
      notifications.show({
        color: 'yellow',
        autoClose: false,
        message: `Račun ${r.stRacuna} ni bil davčno potrjen. Poslan bo samodejno kasneje (ali v podrobnostih računa »Pošlji na FURS«).`,
      });
    }
    setDela(null);
    void navigate(`/racuni/${r.id}`, { replace: true });
  };

  const datumi = { valueFormat: 'D. M. YYYY', clearable: false } as const;
  const vsebina: Record<Korak, React.ReactNode> = {
    kupec: (
      <Stack gap="md">
        {!urejani && !osnutek.stPredracuna && !osnutek.stNaloga && !osnutek.kompletGum && <OdprtiPredracuni />}
        {osnutek.kompletGum && (
          <Alert color="blue" variant="light" icon={<IconFileImport size={18} />}>
            Račun za hrambo gum ({osnutek.kompletGum}): stranka in postavka hrambe sta prevzeti iz kompleta.
          </Alert>
        )}
        {osnutek.stNaloga && (
          <Alert color="blue" variant="light" icon={<IconFileImport size={18} />}>
            Račun iz delovnega naloga {osnutek.stNaloga}: stranka in postavke so prevzete, ob izdaji bo vozilo označeno
            kot prevzeto. Izberite način plačila (gotovina ali kartica se davčno potrdi, za nakazilo je na računu koda
            UPN QR).
          </Alert>
        )}
        {osnutek.stPredracuna && (
          <Alert color="blue" variant="light" icon={<IconFileImport size={18} />}>
            Račun po predračunu {osnutek.stPredracuna}: podatki so prevzeti, ob izdaji bo predračun označen kot vnesen v
            račun.
          </Alert>
        )}
        <OsebeIzdaje
          kupec={kupec}
          uporabnik={uporabnik}
          izberiKupca={(o) => {
            setOsebe((s) => ({ ...s, kupec: o }));
            nastavi({ kupecId: o?.id });
          }}
          izberiUporabnika={(o) => {
            setOsebe((s) => ({ ...s, uporabnik: o }));
            nastavi({ uporabnikVozilaId: o?.id });
          }}
          napake={napake}
          zVozili={(vnos.vozila ?? []).length > 0}
        />
        <Naprej na="artikli" izberi={setKorak} />
      </Stack>
    ),
    artikli: (
      <>
        <VozilaIzdaje
          vozila={vozila}
          dodaj={(v) =>
            nastavi({
              vozila: [...(vnos.vozila ?? []), { voziloId: v.id, cena: v.cena }],
              klavzule: dodajKlavzule(vnos.klavzule ?? [], osnutek.klavzuleZaVozilo ?? []),
            })
          }
          odstrani={(i) => nastavi({ vozila: (vnos.vozila ?? []).filter((_, j) => j !== i) })}
          napaka={napake.vozila ?? napake.postavke}
        />
        <RazlikaVCeni rvc={iz?.rvc ?? []} />
        <Naprej na="storitve" izberi={setKorak} />
      </>
    ),
    storitve: (
      <>
        <StoritveIzdaje
          storitve={storitve}
          dodaj={(s) => nastavi({ storitve: [...(vnos.storitve ?? []), s] })}
          odstrani={(i) => nastavi({ storitve: (vnos.storitve ?? []).filter((_, j) => j !== i) })}
          napaka={napake.storitve ?? napake.postavke}
        />
        <Naprej na="roki" izberi={setKorak} />
      </>
    ),
    roki: (
      <>
        <Kartica naslov="Roki, povezani z računom">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" maw={640}>
            <DatePickerInput
              label="Začetek storitve"
              value={vnos.datumOpravljeneStoritveZacetek ?? null}
              onChange={(d) => nastavi({ datumOpravljeneStoritveZacetek: datumZaApi(d) })}
              error={napake.datumOpravljeneStoritveZacetek}
              {...datumi}
            />
            <DatePickerInput
              label="Konec storitve"
              value={vnos.datumOpravljeneStoritveKonec ?? null}
              onChange={(d) => nastavi({ datumOpravljeneStoritveKonec: datumZaApi(d) })}
              error={napake.datumOpravljeneStoritveKonec}
              {...datumi}
            />
            <DatePickerInput
              label="Datum izdaje"
              value={vnos.datum ?? null}
              onChange={(d) => nastavi({ datum: datumZaApi(d) })}
              error={napake.datum}
              disabled={!!urejani}
              description={urejani ? 'Datum izdanega računa se ne spreminja.' : undefined}
              {...datumi}
            />
            <NumberInput
              label="Rok plačila (dni)"
              value={vnos.rokPlacila ?? 0}
              onChange={(v) => nastavi({ rokPlacila: stevilka(v) ?? 0 })}
              allowDecimal={false}
              min={0}
              error={napake.rokPlacila}
              size="md"
            />
            <NumberInput
              label="Garancija (meseci)"
              value={vnos.stMesecevGarancije ?? 0}
              onChange={(v) => nastavi({ stMesecevGarancije: stevilka(v) ?? 0 })}
              allowDecimal={false}
              min={0}
              error={napake.stMesecevGarancije}
              description={iz?.opisGarancije}
              size="md"
            />
            <div>
              <Text size="sm" fw={500}>
                Plačati do
              </Text>
              <Text size="lg" fw={700} mt={6}>
                {datum(z.datumPlacila) || '–'}
              </Text>
            </div>
          </SimpleGrid>
        </Kartica>
        <Naprej na="predogled" izberi={setKorak} />
      </>
    ),
    predogled: (
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Stack gap="md">
          <Kartica naslov="Kupec in uporabnik vozila">
            <Podatki
              vrstice={[
                ['Kupec', imeOsebe(kupec ?? undefined)],
                ['Uporabnik vozila', imeOsebe(uporabnik ?? undefined)],
                ['Številka', iz?.stevilka],
                ['Plačati do', datum(z.datumPlacila)],
                ['Posredniška', iz?.posredniska ? 'DA' : 'NE'],
                ['Garancija', iz?.opisGarancije],
              ]}
            />
          </Kartica>
          <Kartica naslov="Možnosti računa">
            <Stack gap="xs">
              <Checkbox
                label="Obrnjena davčna obveznost"
                checked={izbira('obrnjenaDavcnaObveznost', iz?.obrnjenaOmogocena)}
                disabled={!iz?.obrnjenaOmogocena}
                onChange={(e) => nastavi({ obrnjenaDavcnaObveznost: e.currentTarget.checked })}
                error={napake.obrnjenaDavcnaObveznost}
              />
              <Checkbox
                label="Posebna ureditev"
                checked={izbira('posebnaUreditev', iz?.posebnaUreditevOmogocena)}
                disabled={!iz?.posebnaUreditevOmogocena}
                onChange={(e) => nastavi({ posebnaUreditev: e.currentTarget.checked })}
              />
              <Checkbox
                label="Karambolirano vozilo"
                checked={izbira('karambolirano', iz?.karamboliranoOmogoceno)}
                disabled={!iz?.karamboliranoOmogoceno}
                onChange={(e) => nastavi({ karambolirano: e.currentTarget.checked })}
              />
              <Checkbox
                label="Vozilo v okvari"
                checked={izbira('voziloVOkvari', iz?.karamboliranoOmogoceno)}
                disabled={!iz?.karamboliranoOmogoceno}
                onChange={(e) => nastavi({ voziloVOkvari: e.currentTarget.checked })}
              />
              {!urejani && (
                <Group gap={4} wrap="nowrap" align="flex-start">
                  <Checkbox
                    label="Financiranje zaloge (leasing)"
                    description="Račun leasing hiši: vozila ostanejo pri vas med financiranimi do odkupa od leasinga."
                    checked={!!vnos.financiranjeZaloge}
                    onChange={(e) => nastavi({ financiranjeZaloge: e.currentTarget.checked })}
                    error={napake.financiranjeZaloge}
                  />
                  <PomocIkona clanek="financiranje-zaloge" />
                </Group>
              )}
              {iz?.izbiraGarancije && (
                <Checkbox
                  label={`Kljub temu izdaj garancijo (${vnos.stMesecevGarancije ?? 0} mes.)`}
                  checked={!!vnos.izdajGarancijo}
                  onChange={(e) => nastavi({ izdajGarancijo: e.currentTarget.checked })}
                  ml="lg"
                />
              )}
              <Group gap={4} wrap="nowrap" align="flex-start">
                <Checkbox
                  label="Plačilo v gotovini (davčno potrjevanje)"
                  checked={izbira('posljiNaFurs')}
                  disabled={!!urejani}
                  onChange={(e) => nastavi({ posljiNaFurs: e.currentTarget.checked })}
                  error={napake.posljiNaFurs}
                />
                <PomocIkona clanek="davcno-potrjevanje" />
              </Group>
              {izbiraProstora && (
                <Select
                  label="Poslovni prostor"
                  data={(furs?.poslovniProstori ?? []).map((p) => ({ value: String(p.id), label: p.naziv ?? '' }))}
                  value={vnos.poslovniProstorId ? String(vnos.poslovniProstorId) : null}
                  onChange={(v) => izberiFurs({ poslovniProstorId: v ? Number(v) : undefined })}
                  error={napake.poslovniProstorId}
                  allowDeselect={false}
                />
              )}
              {izbiraNaprave && (
                <Select
                  label="Elektronska naprava"
                  data={(furs?.naprave ?? []).map((p) => ({ value: String(p.id), label: p.naziv ?? '' }))}
                  value={vnos.elektronskaNapravaId ? String(vnos.elektronskaNapravaId) : null}
                  onChange={(v) => izberiFurs({ elektronskaNapravaId: v ? Number(v) : undefined })}
                  error={napake.elektronskaNapravaId}
                  allowDeselect={false}
                />
              )}
              {!izbiraProstora && napake.poslovniProstorId && (
                <Text size="sm" c="red">
                  {napake.poslovniProstorId}
                </Text>
              )}
              {!izbiraNaprave && napake.elektronskaNapravaId && (
                <Text size="sm" c="red">
                  {napake.elektronskaNapravaId}
                </Text>
              )}
              <Checkbox
                mt="xs"
                label="Izkaži DDV"
                checked={izbira('izkaziDdv', iz?.izkaziDdvOmogocen)}
                disabled={!iz?.izkaziDdvOmogocen}
                onChange={(e) => nastavi({ izkaziDdv: e.currentTarget.checked })}
              />
              <Checkbox
                label="Prepis zakonodaje na računu"
                checked={!!vnos.prepisZakonodaje && !z.obrnjenaDavcnaObveznost}
                disabled={!iz?.prepisOmogocen || !!z.obrnjenaDavcnaObveznost}
                onChange={(e) =>
                  nastavi({ prepisZakonodaje: e.currentTarget.checked ? osnutek.prepisZakonodaje : undefined })
                }
              />
              {vnos.prepisZakonodaje && iz?.prepisOmogocen && !z.obrnjenaDavcnaObveznost ? (
                <Textarea
                  aria-label="Besedilo prepisa"
                  value={vnos.prepisZakonodaje}
                  onChange={(e) => nastavi({ prepisZakonodaje: e.currentTarget.value })}
                  autosize
                  minRows={2}
                  ml="lg"
                />
              ) : (
                <Text size="xs" c="dimmed" ml="lg">
                  {z.izkaziDdv && !z.obrnjenaDavcnaObveznost ? 'DDV je izkazan na računu.' : z.prepisZakonodaje}
                </Text>
              )}
              <Checkbox
                label="Prikaži seznam prilog"
                checked={izbira('prikaziSeznamPriloge')}
                onChange={(e) => nastavi({ prikaziSeznamPriloge: e.currentTarget.checked })}
              />
              <Checkbox
                label="Izpiši uporabnika vozila"
                checked={izbira('izpisiUporabnikaVozila')}
                onChange={(e) => nastavi({ izpisiUporabnikaVozila: e.currentTarget.checked })}
              />
              <Textarea
                mt="xs"
                label="Opombe"
                value={vnos.opombe ?? ''}
                onChange={(e) => nastavi({ opombe: e.currentTarget.value })}
                autosize
                minRows={2}
              />
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                <TextInput
                  label="Referenca za plačilo"
                  description="Prazno = iz številke računa in datuma"
                  value={vnos.referencaZaPlacilo ?? ''}
                  onChange={(e) => nastavi({ referencaZaPlacilo: e.currentTarget.value.toUpperCase() })}
                  maxLength={26}
                  error={napake.referencaZaPlacilo}
                />
                <TextInput
                  label="Koda namena"
                  description="Npr. GDSV"
                  value={vnos.paymentClassification ?? ''}
                  onChange={(e) => nastavi({ paymentClassification: e.currentTarget.value.toUpperCase() })}
                  maxLength={4}
                  error={napake.paymentClassification}
                />
              </SimpleGrid>
            </Stack>
          </Kartica>
        </Stack>
        <Stack gap="md">
          <Kartica naslov="Storitve">
            <TabelaStoritev storitve={storitve} />
          </Kartica>
          <Kartica naslov="Artikli">
            <TabelaVozil vozila={vozila} />
          </Kartica>
          <RazlikaVCeni rvc={iz?.rvc ?? []} />
          <KlavzuleIzdaje klavzule={vnos.klavzule ?? []} nastavi={(k) => nastavi({ klavzule: k })} />
        </Stack>
      </SimpleGrid>
    ),
  };

  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to={urejani ? `/racuni/${urejani}` : '/racuni'}
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        {urejani ? `Račun ${osnutek.stRacuna ?? ''}` : 'Računi'}
      </Button>
      <GlavaZaslona
        naslov={naziv}
        opis={
          urejani
            ? 'Popravite podatke in račun shranite. Pred spremembo se shrani kopija.'
            : 'Izberite kupca, dodajte vozila in storitve, določite roke ter račun preglejte in izdajte.'
        }
      />
      {furs?.opozorilo && z.posljiNaFurs && !urejani && (
        <Alert color="yellow" variant="light" mb="md">
          {furs.opozorilo}
        </Alert>
      )}
      <KorakiIzdaje korak={korak} izberi={setKorak} napake={korakiZNapakami(napake)} vsebina={vsebina} />
      <VrsticaIzdaje
        znesek={iz?.znesek}
        razlog={razlog}
        predogled={() => void predogled()}
        izdaj={() => void izdaj()}
        naziv={urejani ? 'Shrani račun' : 'Izdaj račun'}
        dela={dela}
      />
    </ZaslonSeznama>
  );
}

/** Odprti predračuni (kot UvoziPredracun v Swingu): račun se lahko izda po predračunu, ki prenese njegove podatke. */
function OdprtiPredracuni() {
  const navigate = useNavigate();
  const odprti = useQuery({
    queryKey: ['predracuni', 'odprti'],
    queryFn: ({ signal }) => podatki(api.GET('/api/predracuni/odprti', { signal })),
  });
  const seznam = odprti.data ?? [];
  if (seznam.length === 0) {
    return null;
  }
  return (
    <Kartica naslov="Izdaja po predračunu">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <Select
          label={`Odprti predračuni (${seznam.length})`}
          placeholder="Izberite predračun"
          searchable
          data={seznam.map((p) => ({
            value: String(p.id),
            label: `${p.stPredracuna} · ${imeOsebe(p.kupec)} · ${denar(p.znesek)} €`,
          }))}
          onChange={(v) => v && void navigate(`/racuni/nov?predracun=${v}`)}
          style={{ flex: '1 1 320px' }}
        />
      </Group>
      <Text size="xs" c="dimmed" mt={6}>
        Kupec, uporabnik vozila, postavke in klavzule se prevzamejo s predračuna.
      </Text>
    </Kartica>
  );
}
